import React, {useCallback, useMemo, useState} from 'react';
import type {FieldError, PartInt} from 'schemaDefinition/types';
import {evaluateFieldRequired} from 'schemaDefinition/functions';
import {
  toFieldError,
  validateSingleIntValue,
} from 'schemaDefinition/functions/validators';
import {isNullish} from 'services/utils';
import {TextField} from 'components';
import type {BaseFieldProps} from './types';
import {useShowFieldErrors} from '../../hooks';

type Props = BaseFieldProps & {
  part: PartInt;
};

export const Int: React.FC<Props> = ({
  part,
  value,
  setFieldValue,
  valuePath,
  label,
  placeholder,
  layout,
  mode,
  showWhenReadonlyAndEmpty,
  localScope,
  globalScope,
  relatedScope,
}) => {
  const [visited, setVisited] = useState(false);
  const handleSetVisited = useCallback(() => setVisited(true), []);
  const showFieldErrors = useShowFieldErrors(visited);

  const handleIntChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newVal =
        e.target.value === ''
          ? null
          : isNullish(e.target.value)
            ? null
            : parseInt(e.target.value);
      setFieldValue(valuePath, newVal);
    },
    [valuePath, setFieldValue],
  );

  const required = useMemo(() => {
    return evaluateFieldRequired(
      part.required,
      valuePath,
      localScope,
      globalScope,
      relatedScope,
      value,
    );
  }, [globalScope, part.required, relatedScope, localScope, value, valuePath]);

  const error = useMemo((): FieldError => {
    if (showFieldErrors) {
      const valid = validateSingleIntValue(part, {
        value,
        valuePath,
        localScope,
        globalScope,
        relatedScope,
      }).valid;
      return toFieldError(valid);
    }
    return false;
  }, [
    globalScope,
    part,
    relatedScope,
    localScope,
    showFieldErrors,
    value,
    valuePath,
  ]);
  return (
    <TextField
      name={valuePath}
      label={label}
      placeholder={placeholder}
      layout={layout}
      readonly={mode === 'read-only'}
      showWhenReadonlyAndEmpty={showWhenReadonlyAndEmpty}
      type="number"
      required={required}
      value={value ?? ''}
      onChange={handleIntChange}
      onBlur={handleSetVisited}
      error={error}
    />
  );
};
